import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';

import { identityValidationGateway } from '../../services';
import getClouderStatus from '../../services/getClouderStatus/getClouderStatus';
import {
  IDENTITY_VALIDATION_DECISION,
  IDENTITY_VALIDATION_STATUS,
  isRepeatedManualValidationError,
  mapUserStatusToIdentityValidation,
  normalizeIdentityValidationResult,
  resolveSubmittedIdentityDecision,
} from '../utils/identityValidation';

async function isAlreadyInManualReview(queryClient) {
  try {
    const response = await queryClient.fetchQuery({
      queryKey: ['getClouderStatus'],
      queryFn: getClouderStatus,
    });
    return mapUserStatusToIdentityValidation(response).isPendingManualReview;
  } catch {
    return false;
  }
}

export default function useIdentityValidationFlow({
  state,
  onError,
  gateway = identityValidationGateway,
} = {}) {
  const username = useSelector((store) => store.auth.username);
  const queryClient = useQueryClient();
  const manualAcceptedRef = useRef(false);
  const [status, setStatus] = useState(state?.status ?? null);
  const [dialog, setDialog] = useState(null);
  const [pendingRouteId, setPendingRouteId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [outcome, setOutcome] = useState(IDENTITY_VALIDATION_DECISION.RETRY);

  useEffect(() => {
    if (state?.status !== undefined && state?.status !== null) {
      setStatus(state.status);
    }
  }, [state?.status]);

  const isBlocked = useMemo(
    () =>
      state?.enabled &&
      [
        IDENTITY_VALIDATION_STATUS.WAITING_KYC,
        IDENTITY_VALIDATION_STATUS.WAITING_MANUAL_KYC,
        IDENTITY_VALIDATION_STATUS.WAITING_MANUAL_REVIEW,
        IDENTITY_VALIDATION_STATUS.REJECTED,
      ].includes(status),
    [state?.enabled, status]
  );

  const openValidationFlow = useCallback(
    (routeId = null) => {
      setPendingRouteId(routeId);
      if (status === IDENTITY_VALIDATION_STATUS.WAITING_MANUAL_REVIEW) {
        setDialog('pending');
      } else if (status === IDENTITY_VALIDATION_STATUS.REJECTED) {
        setOutcome(IDENTITY_VALIDATION_DECISION.REJECTED);
        setDialog('error');
      } else if (status === IDENTITY_VALIDATION_STATUS.WAITING_MANUAL_KYC) {
        setDialog('manual');
      } else {
        setDialog('selfie');
      }
    },
    [status]
  );

  const closeValidation = useCallback(async () => {
    if (isSubmitting) return;
    await gateway.cancelValidation();
    setDialog(null);
    setPendingRouteId(null);
  }, [gateway, isSubmitting]);

  const showPendingReview = useCallback(() => {
    manualAcceptedRef.current = true;
    setStatus(IDENTITY_VALIDATION_STATUS.WAITING_MANUAL_REVIEW);
    setDialog('pending');
    queryClient.invalidateQueries({ queryKey: ['getClouderStatus'] });
  }, [queryClient]);

  const submit = useCallback(
    async (request, method) => {
      setIsSubmitting(true);
      try {
        const result = normalizeIdentityValidationResult(
          await gateway[method]({ ...request, id_clouder: username })
        );
        const decision = resolveSubmittedIdentityDecision(result, method);
        if (
          method === 'submitManual' &&
          decision === IDENTITY_VALIDATION_DECISION.PENDING
        ) {
          showPendingReview();
        } else {
          setStatus(result.id_user_status);
        }
        if (decision === IDENTITY_VALIDATION_DECISION.UNKNOWN) {
          Sentry.captureMessage('IDENTITY_VALIDATION_UNKNOWN_STATUS', {
            level: 'warning',
            tags: {
              feature: 'identity_validation',
              action: method,
            },
            extra: {
              id_user_status: result.id_user_status,
              retry: result.retry,
            },
          });
        }
        if (decision === IDENTITY_VALIDATION_DECISION.APPROVED) {
          setDialog('approved');
        } else if (decision === IDENTITY_VALIDATION_DECISION.PENDING) {
          setDialog('pending');
        } else {
          setOutcome(
            decision === IDENTITY_VALIDATION_DECISION.UNKNOWN
              ? IDENTITY_VALIDATION_DECISION.RETRY
              : decision
          );
          setDialog('error');
        }
      } catch (error) {
        const alreadyPending = await isAlreadyInManualReview(queryClient);
        if (
          method === 'submitManual' &&
          (manualAcceptedRef.current ||
            isRepeatedManualValidationError(error) ||
            alreadyPending)
        ) {
          showPendingReview();
          return;
        }
        Sentry.captureException(error, {
          tags: {
            feature: 'identity_validation',
            action: method,
          },
          extra: {
            status: error?.data?.status ?? error?.response?.status,
            url: error?.originalError?.config?.url ?? error?.config?.url,
          },
        });
        onError?.(error);
      } finally {
        setIsSubmitting(false);
      }
    },
    [gateway, onError, queryClient, showPendingReview, username]
  );

  const value = useMemo(
    () => ({
      status,
      dialog,
      outcome,
      isBlocked,
      isSubmitting,
      pendingRouteId,
      openValidationFlow,
      closeValidation,
      submitSelfie: (selfie) => submit({ selfie }, 'submitSelfie'),
      submitManual: ({ selfie, driver_license }) =>
        submit({ selfie, driver_license }, 'submitManual'),
      retrySelfie: () => setDialog('selfie'),
      openManual: () => setDialog('manual'),
      closeDialog: () => setDialog(null),
      approveRoute: () => {
        const routeId = pendingRouteId;
        setPendingRouteId(null);
        setDialog(null);
        return routeId;
      },
    }),
    [
      closeValidation,
      dialog,
      isBlocked,
      isSubmitting,
      outcome,
      pendingRouteId,
      openValidationFlow,
      status,
      submit,
    ]
  );

  return value;
}
